// Wire-level types shared between server and client. Keep these in sync with
// the SQL schema in server/db.ts and the request/response handlers in
// server/{auth,activities,tags}.ts.

export type Visibility = 'private' | 'semi' | 'public' | 'friends';

// --- Auth --------------------------------------------------------------------
export interface SignupRequest {
  email: string;
  // All bytes-fields are base64-encoded for JSON transport.
  auth_salt: string;
  auth_verifier: string;
  kek_salt: string;
  wrapped_dek_pw: string;
  dek_pw_nonce: string;
  rec_salt: string;
  wrapped_dek_rec: string;
  dek_rec_nonce: string;
  // Recovery-side verifier: derived client-side from the recovery code with
  // its own salt, separate from `rec_salt`. The server stores Bun.password.hash
  // of this verifier and uses it to gate recovery-complete; without a valid
  // verifier, recovery-complete is rejected (closing the lockout DoS).
  rec_auth_salt: string;
  rec_auth_verifier: string;
  /**
   * Optional invite token. Required when self-registry is disabled.
   * When present and valid, the new user's `invited_by` is set to the
   * inviter and the invite is marked claimed.
   */
  invite_token?: string;
}

// --- Site settings ---------------------------------------------------------
/** Subset of settings exposed to anonymous clients (gates the signup UI). */
export interface PublicSettings {
  self_registry_enabled: boolean;
}

export interface SettingsUpdateRequest {
  self_registry_enabled?: boolean;
}

// --- Invites ---------------------------------------------------------------
export interface InviteEntry {
  /** The token itself. The shareable URL is built client-side as
   *  `${window.location.origin}/invitasjon/${token}` — server-side URL
   *  construction would point at the API host in split-process dev
   *  environments. NULL when the invite has been claimed; the token has
   *  no functional role after that and we don't keep it in the response. */
  token: string | null;
  created_at: number;
  claimed_at: number | null;
  /** Display name (or username) of the user who claimed it, if any. */
  claimed_by_display: string | null;
}

export interface ChallengeResponse {
  auth_salt: string;
  kek_salt: string;
  wrapped_dek_pw: string;
  dek_pw_nonce: string;
}

export interface LoginRequest {
  email: string;
  auth_verifier: string;
}

export interface RecoveryChallengeResponse {
  rec_salt: string;
  wrapped_dek_rec: string;
  dek_rec_nonce: string;
  // Salt for deriving the recovery verifier (separate from rec_salt above).
  // The client returns the derived verifier on recovery-complete.
  rec_auth_salt: string;
}

export interface PasswordChangeRequest {
  // Requires an active session; new material only — the recovery wrap is untouched.
  auth_salt: string;
  auth_verifier: string;
  kek_salt: string;
  wrapped_dek_pw: string;
  dek_pw_nonce: string;
}

export interface RecoveryCompleteRequest {
  email: string;
  // Proof that the caller knows the recovery code. Must verify against
  // rec_auth_verifier_hash before the server touches any user state.
  rec_auth_verifier: string;
  // New password-side material; the recovery wrap and rec_auth_* are untouched.
  auth_salt: string;
  auth_verifier: string;
  kek_salt: string;
  wrapped_dek_pw: string;
  dek_pw_nonce: string;
}

/** A user's external profile link — social, blog, anything they want. */
export interface UserLink {
  id: string;
  label: string;
  url: string;
}

/** Limits enforced server-side; surfaced so the client UI matches. */
export const USER_LINK_LIMITS = {
  maxPerUser: 5,
  labelMax: 40,
  urlMax: 500,
} as const;

export interface MeResponse {
  id: string;
  email: string;
  display_name: string | null;
  is_moderator: boolean;
  is_admin: boolean;
  username: string | null;
  public_list_enabled: boolean;
  links: UserLink[];
}

// --- Admin -----------------------------------------------------------------
/** A single row in the admin user list. Admin-only. */
export interface AdminUser {
  id: string;
  email: string;
  display_name: string | null;
  username: string | null;
  public_list_enabled: boolean;
  is_moderator: boolean;
  is_admin: boolean;
  created_at: number;
}

/** PATCH /api/admin/users/:id/role payload. Both fields optional. */
export interface AdminRoleUpdate {
  is_moderator?: boolean;
  is_admin?: boolean;
}

export interface ProfileUpdateRequest {
  // All optional — omit a field to leave it alone. Pass `null` to clear
  // display_name or username. The `links` field, when present, replaces
  // the user's entire link list (bulk replace semantics — simpler than
  // tracking per-link ids on the client).
  display_name?: string | null;
  username?: string | null;
  public_list_enabled?: boolean;
  links?: { label: string; url: string }[];
}

/** Response shape for GET /api/users/:username/list (opt-in public list). */
export interface PublicListResponse {
  username: string;
  display_name: string | null;
  links: UserLink[];
  activities: ActivityPublic[];
}

// --- Feedback ---------------------------------------------------------------
export type FeedbackKind = 'feature' | 'bug';

export interface FeedbackSubmitRequest {
  kind: FeedbackKind;
  body: string;
}

export interface FeedbackEntry {
  id: string;
  kind: FeedbackKind;
  body: string;
  created_at: number;
  /** Set when an admin has marked the entry as done. */
  done_at: number | null;
  // The admin who marked the entry done is recorded in the `feedback.done_by`
  // column for audit purposes, but deliberately NOT exposed via the API —
  // moderators don't need to triangulate which admin closed which ticket
  // against the user list. The `done_at` timestamp is sufficient signal.
  // Moderator-only fields; included when the caller is a moderator viewing
  // the list. (The submit endpoint doesn't return these — a submitter doesn't
  // need to see them.)
  user_id?: string;
  user_email?: string;
  user_display?: string | null;
}

export interface FeedbackUpdateRequest {
  done: boolean;
}

// --- Activities --------------------------------------------------------------
export interface ActivityPublic {
  id: string;
  visibility: 'public';
  owner_id: string;             // serialized for public
  /**
   * Public-facing handle: display_name OR (when only the slug is set) the
   * username. NULL when the owner has set neither — in that case the client
   * hides attribution entirely rather than falling back to the email or to
   * an identifier the user didn't choose.
   */
  owner_display: string | null;
  // Owner's URL slug, if they've opted into a public list. When non-null, the
  // client renders the owner attribution as a link to /<owner_username>/liste.
  owner_username: string | null;
  title: string;
  /** Optional free-text body. Plain text. Empty string and null treated the same client-side. */
  description: string | null;
  tags: string[];
  loc_label: string | null;
  loc_lat: number | null;
  loc_lng: number | null;
  scheduled_at: number | null;
  /** Total hearts on this activity. */
  heart_count: number;
  /** True when the authenticated viewer has hearted this activity. */
  viewer_hearted: boolean;
  /** True when the authenticated viewer has bookmarked this activity. */
  viewer_bookmarked: boolean;
  /** Total "done" marks (people who have actually completed the activity). */
  done_count: number;
  /** True when the authenticated viewer has marked this activity done. */
  viewer_done: boolean;
  /** True when the authenticated viewer has archived this activity for
   *  themselves. Default-filtered from the main and public lists; the
   *  client opts in via ?archived=1 to see them. Owner-archiving works too. */
  viewer_archived: boolean;
  /** True when the authenticated viewer has hidden this activity. Only
   *  non-owners can hide. Default-filtered from lists; opt-in via ?hidden=1. */
  viewer_hidden: boolean;
  /** Effective sort position for THIS viewer: a custom value if they've
   *  dragged this row, otherwise -created_at so unsorted/new rows float
   *  to the top. Used by the client to compute drop-target midpoints. */
  sort_position: number;
  created_at: number;
  updated_at: number;
}

export interface ActivitySemi {
  id: string;
  visibility: 'semi';
  // Set ONLY when the viewer is the owner. Lets the client surface
  // Edit/Delete on the user's own semi rows without leaking attribution to
  // anyone else. Stripped server-side for any other viewer; see SECURITY.md.
  owner_id?: string;
  title: string;
  description: string | null;
  tags: string[];
  loc_label: string | null;
  loc_lat: number | null;
  loc_lng: number | null;
  scheduled_at: number | null;
  heart_count: number;
  viewer_hearted: boolean;
  viewer_bookmarked: boolean;
  done_count: number;
  viewer_done: boolean;
  viewer_archived: boolean;
  viewer_hidden: boolean;
  /** Effective sort position for THIS viewer: a custom value if they've
   *  dragged this row, otherwise -created_at so unsorted/new rows float
   *  to the top. Used by the client to compute drop-target midpoints. */
  sort_position: number;
  created_at: number;
  updated_at: number;
}

export interface ActivityPrivate {
  id: string;
  visibility: 'private';
  owner_id: string;        // always you — server only returns your private rows
  ciphertext: string;      // base64
  nonce: string;           // base64
  // Always 0 / false for private rows — hearts and bookmarks don't apply
  // (nobody else sees them; the owner already has direct access). Kept in
  // the type so the client doesn't need a discriminator check before reading.
  heart_count: number;
  viewer_hearted: boolean;
  viewer_bookmarked: boolean;
  // "Done" DOES apply to private rows: the owner can use it as a personal
  // todo checkbox. done_count is therefore always 0 or 1 (just the owner)
  // for private rows. viewer_done reflects the owner's own state.
  done_count: number;
  viewer_done: boolean;
  // Private rows are owner-only, so viewer_hidden is always false (the
  // hide endpoint refuses on your own activities). viewer_archived is the
  // owner archiving their own private todo.
  viewer_archived: boolean;
  viewer_hidden: boolean;
  /** Effective sort position for THIS viewer: a custom value if they've
   *  dragged this row, otherwise -created_at so unsorted/new rows float
   *  to the top. Used by the client to compute drop-target midpoints. */
  sort_position: number;
  created_at: number;
  updated_at: number;
}

/**
 * Friends-only activity. Shape mirrors ActivityPublic — the viewer is
 * definitionally a friend of the owner, so attribution is fine. NOT
 * encrypted; this is an access-list visibility, not a cryptographic one.
 * See /personvern for the trade-off.
 */
export interface ActivityFriends {
  id: string;
  visibility: 'friends';
  owner_id: string;
  owner_display: string | null;
  owner_username: string | null;
  title: string;
  description: string | null;
  tags: string[];
  loc_label: string | null;
  loc_lat: number | null;
  loc_lng: number | null;
  scheduled_at: number | null;
  heart_count: number;
  viewer_hearted: boolean;
  viewer_bookmarked: boolean;
  done_count: number;
  viewer_done: boolean;
  viewer_archived: boolean;
  viewer_hidden: boolean;
  /** Effective sort position for THIS viewer: a custom value if they've
   *  dragged this row, otherwise -created_at so unsorted/new rows float
   *  to the top. Used by the client to compute drop-target midpoints. */
  sort_position: number;
  created_at: number;
  updated_at: number;
}

export type Activity = ActivityPublic | ActivitySemi | ActivityPrivate | ActivityFriends;

// --- Friends & blocks ------------------------------------------------------
/** Both the outgoing-friends list and the incoming-friends list. */
export interface FriendEntry {
  user_id: string;
  username: string | null;
  display_name: string | null;
  since: number;
}

export interface BlockEntry {
  user_id: string;
  username: string | null;
  display_name: string | null;
  since: number;
}

export interface AddByUsernameRequest {
  username: string;
}

export interface CreateActivityRequest {
  visibility: Visibility;
  // For semi/public:
  title?: string;
  description?: string | null;
  tags?: string[];
  loc_label?: string | null;
  loc_lat?: number | null;
  loc_lng?: number | null;
  scheduled_at?: number | null;
  // For private:
  ciphertext?: string;
  nonce?: string;
}

export type UpdateActivityRequest = CreateActivityRequest;

// --- Tags --------------------------------------------------------------------
export interface TagSuggestion {
  name: string;
  usage_count: number;
}
